import bowser from "bowser/bundled";
import { i18n } from "@/main";
/**
 * Merge an array of objects into one.
 *
 * @param {Array<Object>} arr
 * @return {Object}
 */

export function mergeObjects(arr) {
  return arr.reduce((prev, obj) => {
    Object.keys(obj).forEach(key => {
      const existing = prev[key];
      if (existing) {
        // allow multiple mutation objects to contain duplicate
        // handlers for the same mutation type
        if (Array.isArray(existing)) {
          prev[key] = existing.concat(obj[key]);
        } else {
          prev[key] = [existing].concat(obj[key]);
        }
      } else {
        prev[key] = obj[key];
      }
    });
    return prev;
  }, {});
}

/**
 * Get the first item that pass the test
 * by second argument function
 *
 * @param {Array} list
 * @param {Function} f
 * @return {*}
 */
function find(list, f) {
  return list.filter(f)[0];
}

/**
 * Deep copy the given object considering circular structure.
 * This function caches all nested objects and its copies.
 * If it detects circular structure, use cached copy to avoid infinite loop.
 *
 * @param {*} obj
 * @param {Array<Object>} cache
 * @return {*}
 */
export function deepCopy(obj, cache = []) {
  // just return if obj is immutable value
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  // if obj is hit, it is in circular structure
  const hit = find(cache, c => c.original === obj);
  if (hit) {
    return hit.copy;
  }

  const copy = Array.isArray(obj) ? [] : {};
  // put the copy into cache at first
  // because we want to refer it in recursive deepCopy
  cache.push({
    original: obj,
    copy
  });

  Object.keys(obj).forEach(key => {
    copy[key] = deepCopy(obj[key], cache);
  });

  return copy;
}

/**
 * Check whether the given value is Object or not
 *
 * @param {*} obj
 * @return {Boolean}
 */

export function isObject(obj) {
  return obj !== null && typeof obj === "object";
}

/**
 * Get state sub tree by given keys.
 *
 * @param {Object} state
 * @param {Array<String>} nestedKeys
 * @return {Object}
 */
export function getNestedState(state, nestedKeys) {
  return nestedKeys.reduce((state, key) => state[key], state);
}

/**
 * Hacks to get access to Vue internals.
 * Maybe we should expose these...
 */

let Watcher;
export function getWatcher(vm) {
  if (!Watcher) {
    const noop = function() {};
    const unwatch = vm.$watch(noop, noop);
    Watcher = vm._watchers[0].constructor;
    unwatch();
  }
  return Watcher;
}

let Dep;
export function getDep(vm) {
  if (!Dep) {
    Dep = vm._data.__ob__.dep.constructor;
  }
  return Dep;
}

/**
 * Creates a grouped array based upon the specified chunkSize
 */

export function createGroupedArray(arr, chunkSize) {
  var groups = [];
  var i;
  for (i = 0; i < arr.length; i += chunkSize) {
    groups.push(arr.slice(i, i + chunkSize));
  }
  return groups;
}

/**
 * Reorders an array based upon an mutation describing: oldIndex && newIndex
 */

export const reorderArray = (oldIndex, newIndex, originalArray) => {
  const movedItem = originalArray.filter((item, index) => index === oldIndex);
  const remainingItems = originalArray.filter(
    (item, index) => index !== oldIndex
  );

  const reorderedItems = [
    ...remainingItems.slice(0, newIndex),
    movedItem[0],
    ...remainingItems.slice(newIndex)
  ];

  return reorderedItems;
};

/**
 * Get the browser dimensions
 *
 * @return {Object}
 */
export const getWindowDimensions = () => {
  const width = Math.max(
    document.body.offsetWidth,
    document.documentElement.offsetWidth,
    document.documentElement.clientWidth
  );

  const height = Math.max(
    document.body.offsetHeight,
    document.documentElement.offsetHeight,
    document.documentElement.clientHeight
  );

  return { width, height };
};

/**
 * Removes dupes from an array
 * @param Array arrArg
 */
export const uniqueArray = arrArg => {
  return arrArg.filter((elem, pos, arr) => {
    return arr.indexOf(elem) === pos;
  });
};

/**
 * Gets a query string parameter
 * @param {*} name
 */
export const getUrlParam = name => {
  // eslint-disable-next-line
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]')
  var regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
  var results = regex.exec(location.search);
  return results === null
    ? ""
    : decodeURIComponent(results[1].replace(/\+/g, " "));
};

/**
 * Determines whether the user's OS is Mac
 */
export const isMac = () => {
  if (
    window.navigator.platform === "MacIntel" ||
    window.navigator.platform === "MacPPC"
  ) {
    return true;
  }

  return false;
};

/**
 * Converts an array to objects
 * keyed by the given field
 * @param {Array} array
 * @param {String} keyField
 */
export const arrayToObject = (array, keyField) =>
  array.reduce((obj, item) => {
    obj[item[keyField]] = item;
    return obj;
  }, {});

/**
 * Simple browser detection for Firefox
 */
export const isFirefox = () => {
  return (
    typeof InstallTrigger !== "undefined" ||
    navigator.userAgent.indexOf("Firefox") !== -1
  );
};

/**
 * Do we support the user's current browser?
 * @returns {Boolean}
 */
export const isSupportedBrowser = () => {
  // right now we only block IE
  // anything not IE11 with support for flexbox "should be OK"
  // can be updated as necessary
  const browser = bowser.getParser(window.navigator.userAgent);
  const name = browser.getBrowserName();
  if (name && name.toLowerCase() === "internet explorer") {
    return false;
  }

  return true;
};

/**
 * Is the user browsing on a desktop device?
 * @returns {Boolean}
 */
export const isDesktop = () => {
  const browser = bowser.getParser(window.navigator.userAgent);
  const userBrowserInfo = browser.parse();
  const userBrowserInfoParsed = userBrowserInfo.parsedResult;
  return userBrowserInfoParsed.platform.type === "desktop";
};

/**
 * Validates a given password
 * Returns array of errors suitable for user feedback
 * @param {*} inStrPwd
 */
export function BMH_GetPasswordErrors(in_strPwd) {
  let arRejectPwds = [
    `password`,
    `abc123`,
    `qwerty`,
    `123456`,
    `1234567`,
    `12345678`,
    `123456789`,
    `123123`,
    `111111`
  ];

  let arErrorsRes = [];

  if (
    !in_strPwd ||
    in_strPwd === undefined ||
    in_strPwd === `` ||
    typeof in_strPwd !== "string"
  ) {
    arErrorsRes.push(
      new Error(i18n.t("scenes.auth.oPasswordValidation.m_strErrEmpty"))
    );
  } else {
    let iIdxFoundRejectedPwd = arRejectPwds.findIndex(element => {
      return element === in_strPwd;
    });

    if (iIdxFoundRejectedPwd >= 0) {
      arErrorsRes.push(
        new Error(i18n.t("scenes.auth.oPasswordValidation.m_strErrRejectPwds"))
      );
    } else {
      // min 1 numeric character
      // min 1 lowercase character
      // min 1 uppercase character
      // min 6 characters
      // min 1 special character
      // characters not allowed are: SPACE, TAB
      let regexPwd = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.{6,})(?=.*[^a-zA-Z0-9])(?!.*[\t\s])/;

      if (in_strPwd.match(regexPwd) === null) {
        arErrorsRes.push(
          new Error(
            i18n.t("scenes.auth.oPasswordValidation.m_strErrConditions")
          )
        );
      }
    }
  }

  return arErrorsRes;
}
